import React from "react";

// External
import styled, { css } from "styled-components";
import { FiChevronLeft } from "react-icons/fi";
import { navigate } from "gatsby";

// Internal
import useMediaQuery from "../../hooks/use-media-query";
import IconButton from "../../components/form/IconButton";
import { Flex } from "../../styles/global-styles";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";
import { fontFamily, colors, breakpoints, sectionRhythm } from "../../../theme";
import { PROJECT_CONDEXO_PROPERTY_MANAGEMENT } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import Testimonials from "../reviews-section/Testimonials";

// Styles

const BlogContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }

  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const BlogTitle = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const Video = styled.video`
  height: 102%;
  border-radius: 6px !important;
  transform: scale(1.02);

  @media only screen and (min-width: ${breakpoints.tablet}) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  width: ${(props) => (props.isMobileCover ? "96%" : "100%")};
  border: 4px solid white;
  border-radius: 6px !important;
  overflow: hidden;
  box-sizing: border-box;
  background-image: linear-gradient(180deg, ${colors.white}, ${colors.white});
  box-shadow: 17px 17px 48px 0 rgb(0 0 0 / 12%);
  margin-top: 1.5rem;
`;

const BlogContentContainer = styled.div`
  ${caseStudyBodyStyles}
`;
const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  margin-bottom: 0;
`;

const OverviewItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 1000px) {
    flex-direction: row;
  }
`;

const OverviewItem = styled.div`
  margin-bottom: 1rem;

  ${(props) =>
    props.isSeventyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 70%;
      }
    `}
  ${(props) =>
    props.isThirtyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 30%;
      }
    `}
`;
const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;
const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;

  a,
  &[href] {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

/* Architecture */

const boxHelperTypography = css`
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: ${colors.grey.darkMedium};
`;

const diagramHelperText = css`
  ${boxHelperTypography}
  display: block;
`;

const DecisionSection = styled.div`
  margin-top: 0;
`;

const DecisionBlock = styled.div`
  margin-top: 0;
  padding-top: 2rem;

  & > h5 {
    margin-top: 0;
  }
`;

const DecisionTitle = styled.h5`
  margin: 0 0 0.75rem;
  font-family: ${fontFamily.extraBold};
  font-size: 16px;
  line-height: 1.3;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 18px;
  }
`;

const DecisionQuestion = styled.p`
  margin: 1.25rem 0 1.5rem;
  font-weight: 600;
`;

const DecisionFlowArrow = styled.div`
  width: 1px;
  height: 24px;
  margin: 0 auto;
  background: black;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 7px;
    height: 7px;
    border-right: 1px solid black;
    border-bottom: 1px solid black;
    transform: translateX(-50%) rotate(45deg);
    background: transparent;
  }
`;

const DecisionComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 2rem 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DecisionCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 10px;
  background: ${colors.white};

  h6 {
    margin: 0 0 0.75rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  ul,
  ol {
    margin: 0;
    padding-left: 1.25rem;
    list-style: none;
  }

  li {
    ${boxHelperTypography}
    display: list-item;
    margin: 0 0 0.45rem 1rem;
    padding: 0;
    letter-spacing: -0.003em;
    list-style-position: outside;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 14px;
      line-height: 1.45;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li {
    list-style-type: disc;
  }

  ol > li {
    list-style-type: decimal;
  }
`;

const ArchitectureDiagram = styled.div`
  margin: 2.5rem 0 3rem;
  padding: 1.25rem 0;
  box-sizing: border-box;
`;

const ArchitectureNode = styled.div`
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
  padding: 0.9rem 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  text-align: center;
  background: ${colors.white};
  box-sizing: border-box;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 3px;
  }
`;

const ArchitectureArrow = styled(DecisionFlowArrow)`
  height: 28px;
`;

const ArchitectureSplit = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(
      ${(props) => props.$columns || 2},
      minmax(0, 1fr)
    );
    gap: 2.5rem;
  }
`;

const ArchitectureBranch = styled.div`
  position: relative;
  padding-top: 0;

  &:first-child {
    padding-top: 1.5rem;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    padding-top: 1.5rem;
  }
`;

const ArchitectureLabel = styled.div`
  margin-bottom: 0.8rem;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
  color: ${colors.grey.darkMedium};
`;

const ArchitectureStack = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const ArchitectureStackItem = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  background: ${colors.white};

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 0.2rem;
  }
`;

const ProductDomainMap = () => (
  <ArchitectureDiagram>
    <ArchitectureNode>
      <strong>Property management platform</strong>
      <small>One app for rental day to day</small>
    </ArchitectureNode>

    <ArchitectureArrow />

    <ArchitectureSplit $columns={3}>
      <ArchitectureBranch>
        <ArchitectureLabel>Landlord</ArchitectureLabel>
        <ArchitectureStack>
          <ArchitectureStackItem>
            <strong>Properties &amp; tenancies</strong>
            <small>Managing the portfolio</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Payments &amp; accounts</strong>
            <small>Rent and accounts</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Documents &amp; scheduling</strong>
            <small>Files, calendar, and planning</small>
          </ArchitectureStackItem>
        </ArchitectureStack>
      </ArchitectureBranch>

      <ArchitectureBranch>
        <ArchitectureLabel>Tenant</ArchitectureLabel>
        <ArchitectureStack>
          <ArchitectureStackItem>
            <strong>Rent payments</strong>
            <small>Paying and tracking rent</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Requests &amp; communication</strong>
            <small>Maintenance and messaging</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Documents</strong>
            <small>Contracts and files</small>
          </ArchitectureStackItem>
        </ArchitectureStack>
      </ArchitectureBranch>

      <ArchitectureBranch>
        <ArchitectureLabel>Contractor</ArchitectureLabel>
        <ArchitectureStack>
          <ArchitectureStackItem>
            <strong>Jobs &amp; schedule</strong>
            <small>Assigned jobs and timing</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Communication</strong>
            <small>Updates with landlords and tenants</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Notifications</strong>
            <small>Job and status alerts</small>
          </ArchitectureStackItem>
        </ArchitectureStack>
      </ArchitectureBranch>
    </ArchitectureSplit>
  </ArchitectureDiagram>
);

const FrontendFoundationDiagram = () => (
  <ArchitectureDiagram>
    <ArchitectureNode>
      <strong>React Application</strong>
      <small>Frontend organised by feature</small>
    </ArchitectureNode>

    <ArchitectureArrow />

    <ArchitectureSplit>
      <ArchitectureBranch>
        <ArchitectureLabel>Feature layer</ArchitectureLabel>
        <ArchitectureStack>
          <ArchitectureStackItem>
            <strong>Property management</strong>
            <small>Property workflows</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Tenancy</strong>
            <small>Tenant and contractor workflows</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Payments</strong>
            <small>Payment-related UI and flows</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Calendar / Chat / Notifications</strong>
            <small>Custom product flows</small>
          </ArchitectureStackItem>
        </ArchitectureStack>
      </ArchitectureBranch>

      <ArchitectureBranch>
        <ArchitectureLabel>Shared foundation</ArchitectureLabel>
        <ArchitectureStack>
          <ArchitectureStackItem>
            <strong>Redux</strong>
            <small>Shared application state</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>Redux Saga</strong>
            <small>Async service workflows</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>React Forms + Yup</strong>
            <small>Forms and validation</small>
          </ArchitectureStackItem>
          <ArchitectureStackItem>
            <strong>React Bootstrap + Stripe</strong>
            <small>UI kit and payments</small>
          </ArchitectureStackItem>
        </ArchitectureStack>
      </ArchitectureBranch>
    </ArchitectureSplit>
  </ArchitectureDiagram>
);

const CondexoPropertyManagementProjectSection = () => {
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.tablet} )`);

  const goBack = () => {
    navigate(-1);
  };

  const randomData = {
    date: new Date(),
    title:
      "Reducing landlord's burden of handling properties using property management web app",
    clientName: "Condexo",
    projectName: "Condexo Property Management",
    role: "React developer",
    year: "2019-2021",
    cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039284/projects/condexo-property-management/condexo-property__dashboard_fso9cu.mp4",
    mobile_cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039329/projects/condexo-property-management/condexo-property__dashboard--mobile_nz3wn6.mp4",
    clientCountry: "United Kingdom",
    servicesProvided: "Front-end development",
    industry: "Real estate",
    techStack: [
      "Javascript",
      "React",
      "Redux",
      "Redux Saga",
      "React Bootstrap",
      "Stripe",
    ],
    projectLink: "https://manager.condexo.co.uk/",
    clientReviews: [
      {
        name: "Elisa Fiorenzani",
        position: "UX/UI designer",
        company: "Condexo",
        profilePic: "elisa-condexo.jpeg",
        text: "It was a pleasure to work with Parmeet, an honest and reliable Dev!",
      },
    ],
  };

  const mediaLinks = {
    property:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038639/projects/condexo-property-management/condexo-pay__property_kqopqo_itjn26.mp4",
    tenancy:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038690/projects/condexo-property-management/condexo-pay__create-tenancy_jq9qvl_ahn2ls.mp4",
    calendar:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039494/projects/condexo-property-management/condexo-property__calendar_mbspuo.mp4",
    documents:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039583/projects/condexo-property-management/condexo-property__documents_k2yytp.mp4",
    accounts:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038682/projects/condexo-property-management/condexo-property__accounts_s542wo_owaoka.mp4",
  };

  return (
    <>
      <BlogContainer>
        {isMobile && (
          <Flex spaceBetween>
            <IconButton
              bgColor="#bbb"
              iconColor="#000"
              style={{ marginLeft: "-12px" }}
              onClick={goBack}
              ariaLabel="Go back"
            >
              <FiChevronLeft />
            </IconButton>
            {/* <SocialMediaButtons shareLinks={shareLinks} isMobile={true} /> */}
          </Flex>
        )}
        <ContainerScroll
          titleComponent={<BlogTitle>{randomData.title}</BlogTitle>}
          role={randomData.role}
        >
          <ProjectHero
            src={PROJECT_CONDEXO_PROPERTY_MANAGEMENT.media.hero}
            alt={randomData.title}
          />
        </ContainerScroll>
        <OverviewSection>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{randomData.clientName}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{randomData.clientCountry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{randomData.servicesProvided}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{randomData.industry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          {isMobile && (
            <OverviewItem isHundredPercent>
              <OverviewLabel>Year</OverviewLabel>
              <OverviewValue>{randomData.year}</OverviewValue>
            </OverviewItem>
          )}
          <OverviewItem isHundredPercent>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{randomData.techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {randomData.projectLink ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={randomData.projectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {randomData.projectLink}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>
        <BlogContentContainer>
          <section>
            <h4>About the product</h4>

            <p>
              Condexo Property Management brought day-to-day rental management
              into one place for landlords, tenants, and contractors. Payments,
              communication, maintenance, documents, and scheduling were meant
              to live in a single application instead of a pile of separate
              tools.
            </p>

            <p>
              This was my second project with Condexo, after shipping the MVP
              for{" "}
              <a
                href="/projects/condexo-pay"
                target="_blank"
                rel="noopener noreferrer"
              >
                Condexo Pay
              </a>
              . I joined as a frontend developer to help build the
              property-management MVP. That meant turning the client's designs
              into React, building the product workflows, integrating APIs and
              payments, and helping decide how the frontend should be put
              together.
            </p>

            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.property} />
            </VideoContainer>
          </section>

          <section>
            <h4>What we were building</h4>

            <p>
              The product had to work for three kinds of users on one platform.
              Landlords needed to manage portfolios, tenants needed to pay rent
              and raise requests, and contractors needed to coordinate jobs.
              Each group needed different things, but it still had to feel like
              one property management system.
            </p>

            <p>
              The frontend team was a couple of React developers, including me,
              working with backend developers and a project manager. The
              client's in-house designer provided the screens. Their development
              team reviewed the work before it went to production.
            </p>

            <ProductDomainMap />

            <p>
              The MVP had to cover a lot, including properties and tenancies,
              payments, calendar, chat, documents, and notifications. A small
              frontend team also needed to keep adding to it without stepping on
              each other. So we had to decide where to add structure, what to
              take off the shelf, and where custom work was actually worth it.
            </p>

            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.tenancy} />
            </VideoContainer>
          </section>

          <section>
            <h4>Engineering Decisions</h4>

            <p>
              For each of these, we started from a real product problem, looked
              at the options, and picked what fit the MVP best. If something was
              already a solved problem, we used an existing solution. Custom
              work went into the Condexo-specific workflows.
            </p>

            <DecisionSection>
              <DecisionBlock>
                <DecisionTitle>
                  1. Structure: feature / container folders
                </DecisionTitle>

                <p>
                  The app was going to grow across properties, tenancies,
                  payments, calendar, and communication. More than one frontend
                  developer would be working at once. If everything lived in
                  generic shared folders, related code would scatter and people
                  would keep bumping into the same files.
                </p>

                <DecisionQuestion>
                  How should we organise the codebase so features stay separate
                  and developers can work in parallel without getting in each
                  other's way?
                </DecisionQuestion>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>File-type / layer folders</h6>
                    <ul>
                      <li>Easy to set up at the start</li>
                      <li>Familiar components, reducers, and sagas layout</li>
                      <li>Code for one feature ends up spread around</li>
                      <li>Shared folders become places people collide</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Feature / container folders</h6>
                    <ul>
                      <li>Clearer feature boundaries</li>
                      <li>Related UI and behaviour stay together</li>
                      <li>Easier to own work between developers</li>
                      <li>Holds up better as more features land</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Hybrid shared core + features</h6>
                    <ul>
                      <li>Shared pieces live in a common layer</li>
                      <li>Features still own most product behaviour</li>
                      <li>Needs clearer rules for what is shared</li>
                      <li>More to agree on before the MVP ships</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  We went with feature and container folders. It kept ownership
                  clear as the product grew, which mattered more than starting
                  with a simpler file-type layout.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>2. Forms: React Forms + Yup</DecisionTitle>

                <p>
                  Landlords, tenants, and contractors all had forms. They were
                  creating properties and tenancies, collecting details,
                  validating them, and sending them off. If we built field
                  state, validation, errors, and submit behaviour ourselves for
                  every form, we would spend the MVP on plumbing instead of the
                  product.
                </p>

                <DecisionQuestion>
                  With so many forms and a tight MVP, how should we handle form
                  state and validation?
                </DecisionQuestion>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Build in-house</h6>
                    <ul>
                      <li>Full control over how forms work</li>
                      <li>More work before anything ships</li>
                      <li>We own validation and how forms run</li>
                      <li>More code to keep consistent</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Formik</h6>
                    <ul>
                      <li>Popular way to manage form state in React</li>
                      <li>Works well with schema validation libraries</li>
                      <li>Still need to choose and wire up validation</li>
                      <li>More to learn than we needed for MVP forms</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>React Final Form</h6>
                    <ul>
                      <li>Updates form state through subscriptions</li>
                      <li>Good performance story for large forms</li>
                      <li>Harder for the team to pick up</li>
                      <li>More than we needed for the MVP forms</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>React Forms + Yup</h6>
                    <ul>
                      <li>Handles form state without us building it</li>
                      <li>Yup schemas keep validation explicit and consistent</li>
                      <li>Less repeated work across many forms</li>
                      <li>Faster to ship with the MVP</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  We chose React Forms with Yup. It gave us consistent
                  validation and less repeated form code, without us owning a
                  form library of our own. We gave up some control. That was
                  fine for the MVP.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>
                  3. UI: React Bootstrap + custom where needed
                </DecisionTitle>

                <p>
                  The UI was a mix. Tables, modals, and form layouts were
                  ordinary. Calendar, chat, notifications, and rent payments
                  were not. Building every component ourselves would have taken
                  too long. Pushing the product-specific bits into a generic
                  component kit would have made those flows worse.
                </p>

                <DecisionQuestion>
                  How do we ship ordinary UI quickly and still build the
                  property-specific parts properly?
                </DecisionQuestion>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Build all UI custom</h6>
                    <ul>
                      <li>Full control over every interaction</li>
                      <li>Most work before screens ship</li>
                      <li>We own more design and accessibility ourselves</li>
                      <li>Slow on ordinary screens</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Material UI (full kit)</h6>
                    <ul>
                      <li>Large set of ready-made components</li>
                      <li>Good defaults for ordinary UI</li>
                      <li>Heavier look to adopt and override</li>
                      <li>Harder when a workflow needs custom behaviour</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>React Bootstrap + custom</h6>
                    <ul>
                      <li>Solid building blocks for ordinary UI</li>
                      <li>Lighter to take on in an MVP React app</li>
                      <li>Room to build calendar, chat, and similar flows</li>
                      <li>Clear split between reuse and custom work</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  We used React Bootstrap for the ordinary screens and built
                  calendar and chat ourselves. Notifications and rent payments
                  were treated as product features, not just styled components.
                  We did not get a complete design system the way a full kit
                  like Material UI would have given us. We did get faster
                  standard screens and room to build the unusual ones.
                </p>

                <VideoContainer>
                  <Video autoPlay muted loop src={mediaLinks.calendar} />
                </VideoContainer>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>4. Payments: Stripe</DecisionTitle>

                <p>
                  Rent and other payments sat at the centre of the product. We
                  needed something reliable that worked in a React web app, did
                  not put card data in our own systems, and could ship with the
                  MVP.
                </p>

                <DecisionQuestion>
                  How should the product take payments without us building a
                  payments stack?
                </DecisionQuestion>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Build on bank / processor APIs</h6>
                    <ul>
                      <li>Full control over the payment path</li>
                      <li>We own more of the security and ops work</li>
                      <li>Longer to integrate and certify</li>
                      <li>Too slow for the MVP</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Alternative payment provider</h6>
                    <ul>
                      <li>Could cover basic card payments</li>
                      <li>React or web SDK quality varies by provider</li>
                      <li>Docs and ops tools are often thinner</li>
                      <li>Less predictable for MVP payment flows</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Stripe</h6>
                    <ul>
                      <li>Mature payments setup and web SDK</li>
                      <li>Straightforward to use from a React frontend</li>
                      <li>Card data stays with Stripe, not in our app</li>
                      <li>Quickest reliable way to take payments in the MVP</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  We chose Stripe. It was the strongest option for reliability,
                  React integration, keeping card data out of our app, and
                  shipping on time. The frontend wired payments into the rest of
                  the product. Stripe ran the payment service.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>Frontend foundation</DecisionTitle>

                <p>
                  These choices gave us a frontend with clear feature ownership,
                  off-the-shelf pieces where they fit, and custom work where the
                  product needed it.
                </p>

                <FrontendFoundationDiagram />

                <p>
                  That was enough to grow properties, tenancies, payments,
                  calendar, chat, and notifications without rebuilding the stack
                  each time.
                </p>

                <VideoContainer>
                  <Video autoPlay muted loop src={mediaLinks.documents} />
                </VideoContainer>
              </DecisionBlock>
            </DecisionSection>
          </section>

          <section>
            <h4>Outcome</h4>

            <p>
              The MVP went live in May 2020. It got the property-management
              product into production and helped bring more investors onto the
              project.
            </p>

            <p>
              Work continued after that. Version 2.0 shipped in February 2021
              with new features and a better experience as the product grew.
            </p>

            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.accounts} />
            </VideoContainer>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials
              showMore={false}
              reviews={PROJECT_CONDEXO_PROPERTY_MANAGEMENT.client.reviews}
            />
          </section>
        </BlogContentContainer>
      </BlogContainer>
      <ContactMeSection />
    </>
  );
};

export default CondexoPropertyManagementProjectSection;
